import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { GoogleButton } from "@/components/GoogleButton";
import { ArrowRight } from "lucide-react";

export default function SignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const next = new URLSearchParams(loc.search).get("next") || "/";

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(next);
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">
      <div className="w-full max-w-md relative z-[2]">
        <div className="text-center mb-8">
          <div className="kpi-label">Sign In</div>
          <h1 className="display text-4xl mt-2 font-bold uppercase tracking-tight leading-none">
            Command<span className="text-oud">·</span>Center
          </h1>
          <div className="hairline-oud w-24 mx-auto mt-4" />
          <div className="serif-mark text-sm text-muted-foreground mt-3 tracking-wide">
            For CEO's Only
          </div>
        </div>

        <form onSubmit={submit} className="hud-frame p-8 space-y-5" data-testid="signin-form">
          <div className="tread-plate h-1.5 -mx-8 -mt-8 mb-4" />
          <div>
            <div className="kpi-label">Email</div>
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="hud-input mt-2"
              placeholder="agent@example.com"
              required
              data-testid="signin-email"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <div className="kpi-label">Password</div>
              <Link
                to="/forgot-password"
                className="mono text-[9px] uppercase tracking-widest text-muted-foreground hover:text-hud"
                data-testid="signin-forgot-link"
              >
                Forgot?
              </Link>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="hud-input mt-2"
              placeholder="••••••••"
              required
              data-testid="signin-password"
            />
          </div>
          {err && (
            <div className="text-[hsl(var(--oxblood))] mono text-xs uppercase tracking-widest" data-testid="signin-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="hud-btn hud-btn-primary w-full justify-center"
            data-testid="signin-submit"
          >
            {busy ? "Signing in…" : "SIGN IN"} <ArrowRight size={11} />
          </button>

          <div className="flex items-center gap-3 my-2">
            <div className="flex-1 h-px bg-border" />
            <span className="mono text-[9px] uppercase tracking-[0.32em] text-muted-foreground">OR</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <GoogleButton next={next} label="Continue with Google" />
        </form>

        <div className="mt-5 text-center mono text-[11px] uppercase tracking-widest text-muted-foreground">
          New operator?{" "}
          <Link to="/signup" className="text-foreground hover:text-hud" data-testid="signin-to-signup">
            Create account
          </Link>
        </div>
      </div>
    </div>
  );
}
